import React, { useState } from 'react';
import { Receipt, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const MIN_REASON = 10;

export const AdvanceInvoiceDialog = ({ open, onOpenChange, order, onIssued }) => {
  const [reason, setReason] = useState('');
  const [issuing, setIssuing] = useState(false);

  const handleConfirm = async () => {
    setIssuing(true);
    try {
      const result = await adminService.issueAdvanceInvoice(order.id, reason.trim());
      toast.success(`Invoice ${result.invoice_number} issued — the client can pay now`);
      setReason('');
      onOpenChange(false);
      onIssued?.(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue the advance invoice');
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!issuing) { onOpenChange(o); if (!o) setReason(''); } }}>
      <DialogContent className="max-w-lg" data-testid="advance-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-slate-600" />
            Invoice {order?.order_number} in advance
          </DialogTitle>
          <DialogDescription>
            This is an exception, not a routine step.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <AlertTriangle className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
            <p className="text-sm text-slate-700 leading-relaxed">
              This creates an invoice now, before delivery. The client will be able to pay
              immediately. Quantities are billed as ordered. Any difference at delivery will
              be reconciled with a credit memo.
            </p>
          </div>

          <div>
            <Label htmlFor="advance-reason" className="text-xs uppercase text-gray-500">
              Reason (required)
            </Label>
            <Textarea
              id="advance-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Client requested to prepay this catering order before the event"
              className="mt-1"
              data-testid="advance-invoice-reason"
            />
            <p className="text-xs text-gray-400 mt-1">
              {reason.trim().length < MIN_REASON
                ? `${MIN_REASON - reason.trim().length} more characters needed`
                : 'Recorded on the invoice and in the audit log'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={issuing}
            data-testid="advance-invoice-cancel"
          >
            Cancel
          </Button>
          <Button
            className="bg-slate-800 hover:bg-slate-900"
            onClick={handleConfirm}
            disabled={issuing || reason.trim().length < MIN_REASON}
            data-testid="advance-invoice-confirm"
          >
            {issuing ? 'Issuing…' : 'Issue advance invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
